import { useState, type FC } from "react";

import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { CustomMultiSelect } from "@/shared/components/CustomMultiSelect";
import { ONBOARDING_RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import {
  PERSON_STATUS_OPTIONS,
  ESTATE_ROLE_OPTIONS,
} from "@/shared/constants/contactDetailOptions";

import type { PersonContact } from "@/shared/types/estateContact";
import { EditSectionShell } from "./EditSectionShell";

interface PersonalDetailsEditProps {
  contact: PersonContact;
  deceasedFirstName: string;
  onCancel: () => void;
  onSave: (updates: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
}

export const PersonalDetailsEdit: FC<PersonalDetailsEditProps> = ({
  contact,
  deceasedFirstName,
  onCancel,
  onSave,
  isSaving,
}) => {
  const [firstName, setFirstName] = useState(contact.first_name || "");
  const [middleName, setMiddleName] = useState(contact.middle_name || "");
  const [lastName, setLastName] = useState(contact.last_name || "");
  const [nickname, setNickname] = useState(contact.nickname || "");
  const [relationship, setRelationship] = useState(contact.relationship || "");
  const [estateRole, setEstateRole] = useState<Array<{ value: string; label: string }>>(
    (contact.legal_role || []).map((r) => {
      const opt = ESTATE_ROLE_OPTIONS.find((o) => o.value === r);
      return opt || { value: r, label: r };
    }),
  );
  const [status, setStatus] = useState(contact.person_status || "");

  const handleSave = () => {
    onSave({
      first_name: firstName.trim() || null,
      middle_name: middleName.trim() || null,
      last_name: lastName.trim() || null,
      nickname: nickname.trim() || null,
      relationship: relationship || null,
      legal_role: estateRole.map((r) => r.value),
      person_status: status || null,
    });
  };

  return (
    <EditSectionShell
      title="Personal details"
      onCancel={onCancel}
      onSave={handleSave}
      isSaving={isSaving}
    >
      <div className="flex gap-4">
        <div className="flex-1 flex flex-col gap-1.5">
          <Label>First name</Label>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="flex-1 flex flex-col gap-1.5">
          <Label>Middle name</Label>
          <Input value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex-1 flex flex-col gap-1.5">
          <Label>Last name</Label>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <div className="flex-1 flex flex-col gap-1.5">
          <Label>Nickname</Label>
          <Input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Nickname"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Relationship to {deceasedFirstName}</Label>
        <Select value={relationship} onValueChange={setRelationship}>
          <SelectTrigger className="w-full font-normal">
            <SelectValue placeholder="Select relationship" />
          </SelectTrigger>
          <SelectContent>
            {ONBOARDING_RELATIONSHIP_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Estate role</Label>
        <CustomMultiSelect
          options={ESTATE_ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          selected={estateRole}
          onChange={setEstateRole}
          placeholder="Select roles"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full font-normal">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            {PERSON_STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </EditSectionShell>
  );
};
